import defineBindingContexts from './binding-contexts';
import defineBindingContextTypes from './binding-context-types';
import defineBindableProperties from './binding-sources';
import defineRelationships from './relationships';

import type {
  DataSourceDefinition,
  DataSourceDependencies,
  TypeReferenceBuilder,
  BindingContextDef,
  BindingContextRelationshipDef,
  BindablePropertiesDef,
} from '@packages/systems/dynamo/source-types';
import {isCommerceCollection} from '@packages/systems/commerce/utils/CommerceCollectionUtils';
import {Plugins} from '@packages/systems/wfdl';
import type {Collections} from '@packages/systems/dynamo/records/CollectionRecord';

const onlyEcommerceCollections = (collections: Collections): Collections =>
  collections.filter(isCommerceCollection);

export const dataSourceDefinition: DataSourceDefinition = {
  pluginName: Plugins.name('Commerce'),
  defineTypes(dependencies: DataSourceDependencies) {
    const collections = dependencies.getCollections();
    const ecommerceCollections =
      collections && onlyEcommerceCollections(collections);
    return ecommerceCollections && ecommerceCollections.size > 0
      ? defineBindingContextTypes(ecommerceCollections)
      : [];
  },

  defineBindingContexts<RootTRef, EntityTRef, ListTRef>(
    dependencies: DataSourceDependencies,
    referenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>
  ): BindingContextDef<RootTRef, EntityTRef, ListTRef>[] {
    const collections = dependencies.getCollections();
    const ecommerceCollections =
      collections && onlyEcommerceCollections(collections);
    return ecommerceCollections && ecommerceCollections.size > 0
      ? defineBindingContexts(ecommerceCollections, referenceBuilder)
      : [];
  },

  defineRelationships<RootTRef, EntityTRef, ListTRef>(
    dependencies: DataSourceDependencies,
    referenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>
  ): BindingContextRelationshipDef<RootTRef, EntityTRef>[] {
    const collections = dependencies.getCollections();
    const ecommerceCollections =
      collections && onlyEcommerceCollections(collections);
    return ecommerceCollections && ecommerceCollections.size > 0
      ? defineRelationships(ecommerceCollections, referenceBuilder)
      : [];
  },

  defineBindableProperties<RootTRef, EntityTRef, ListTRef>(
    dependencies: DataSourceDependencies,
    referenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>
  ): BindablePropertiesDef<EntityTRef, ListTRef>[] {
    const collections = dependencies.getCollections();
    const ecommerceCollections =
      collections && onlyEcommerceCollections(collections);
    const isDesigner = dependencies.runtimeEnvironment.isDesigner();
    return ecommerceCollections && ecommerceCollections.size > 0
      ? defineBindableProperties(
          ecommerceCollections,
          referenceBuilder,
          isDesigner
        )
      : [];
  },
};
